<script setup lang="ts">
    import { ref } from 'vue'
    import { useI18n } from 'vue-i18n'
    import { useAlert } from '@volverjs/ui-vue/composables'
    import PkEditorCode from './PkEditorCode.vue'
    import PkFieldset from './PkFieldset.vue'
    import PkSchemaField from './PkSchemaField.vue'
    import draggable from 'vuedraggable'
    import type { JsonSchema, SchemaField } from 'composables'
    import { useJsonSchemaEditor, fieldsToSchema } from 'composables'

    withDefaults(
        defineProps<{
            label?: string
            placeholder?: string
            allowArrayRoot?: boolean
        }>(),
        {
            label: undefined,
            placeholder: undefined,
            allowArrayRoot: false,
        },
    )

    const modelValue = defineModel<JsonSchema | null | undefined>()

    const { t: $t } = useI18n({ useScope: 'global' })
    const { addAlert } = useAlert()

    const {
        fields,
        codeValue,
        rootType,
        syncAll,
        updateFromCode,
        setRootType,
        addField,
        updateField,
        removeField,
    } = useJsonSchemaEditor(modelValue)

    // ─── Cross-list drag dedup ──────────────────────────────────────────────────

    function removeFromNested(items: SchemaField[], id: string): SchemaField[] {
        return items.map((item) => ({
            ...item,
            children: removeFromNested(
                item.children.filter((c) => c.id !== id),
                id,
            ),
        }))
    }

    const onRootDragChange = (evt: {
        added?: { element: SchemaField; newIndex: number }
    }) => {
        if (evt.added) {
            const { element, newIndex } = evt.added
            let updated = removeFromNested(fields.value, element.id)
            if (!updated.some((f) => f.id === element.id)) {
                updated = [
                    ...updated.slice(0, newIndex),
                    element,
                    ...updated.slice(newIndex),
                ]
            }
            fields.value = updated
        }
    }

    // ─── Tab toggle ───────────────────────────────────────────────────────────────

    type Tab = 'visual' | 'code'
    const activeTab = ref<Tab>('visual')

    const onSwitchTab = (tab: Tab) => {
        if (tab === activeTab.value) {
            return
        }
        if (tab === 'visual') {
            try {
                const parsed = codeValue.value
                    ? (JSON.parse(codeValue.value) as JsonSchema)
                    : null
                syncAll(parsed)
            } catch {
                addAlert({
                    modifiers: 'danger',
                    title: $t('message.error'),
                    content: $t('message.invalidJson'),
                })
                return
            }
        }
        if (tab === 'code') {
            const schema =
                fields.value.length > 0
                    ? fieldsToSchema(fields.value, rootType.value)
                    : null
            codeValue.value = schema ? JSON.stringify(schema, null, 2) : ''
        }
        activeTab.value = tab
    }

    defineExpose({ syncAll })
</script>

<template>
    <div
        class="pk-editor-json-schema"
        :class="{ 'pk-editor-json-schema--empty': fields.length === 0 }">
        <PkFieldset :legend="label" class="relative">
            <div class="pk-editor-json-schema__header">
                <VvButtonGroup
                    v-if="allowArrayRoot"
                    modifiers="compact"
                    class="pk-editor-json-schema__root-type">
                    <VvButton
                        modifiers="action-quiet-small"
                        :active="rootType === 'object'"
                        :label="$t('label.schemaRootObject')"
                        icon="ri:braces-line"
                        @click="setRootType('object')" />
                    <VvButton
                        modifiers="action-quiet-small"
                        :active="rootType === 'array'"
                        :label="$t('label.schemaRootArray')"
                        icon="ri:brackets-line"
                        @click="setRootType('array')" />
                </VvButtonGroup>
                <VvButtonGroup
                    modifiers="compact"
                    class="pk-editor-json-schema__tabs">
                    <VvButton
                        modifiers="action-quiet-small"
                        :active="activeTab === 'visual'"
                        :label="$t('label.visualEditor')"
                        icon="ri:layout-grid-line"
                        @click="onSwitchTab('visual')" />
                    <VvButton
                        modifiers="action-quiet-small"
                        :active="activeTab === 'code'"
                        :label="$t('label.codeEditor')"
                        icon="ri:code-line"
                        @click="onSwitchTab('code')" />
                </VvButtonGroup>
            </div>

            <!-- Visual tab -->
            <div
                v-if="activeTab === 'visual'"
                class="pk-editor-json-schema__visual-editor">
                <div
                    v-if="allowArrayRoot && rootType === 'array'"
                    class="pk-editor-json-schema__array-hint">
                    {{ $t('label.arrayOfObjects') }}
                </div>
                <draggable
                    v-model="fields"
                    item-key="id"
                    handle=".drag-handle"
                    :group="{ name: 'schema-fields' }"
                    @change="onRootDragChange">
                    <template #item="{ element, index }">
                        <PkSchemaField
                            :field="element"
                            @update:field="updateField(index, $event)"
                            @remove="removeField(index)" />
                    </template>
                </draggable>
            </div>

            <!-- Code tab -->
            <div v-else class="pk-editor-json-schema__code-editor">
                <PkEditorCode
                    :model-value="codeValue"
                    language="json"
                    :placeholder="placeholder"
                    @update:model-value="updateFromCode" />
            </div>

            <!-- Actions -->
            <div
                v-if="activeTab === 'visual' || $slots.actions"
                class="pk-editor-json-schema__actions">
                <slot name="actions" :sync-all="syncAll" />
                <VvButton
                    v-if="activeTab === 'visual'"
                    modifiers="action-quiet-small"
                    icon="ri:add-line"
                    icon-position="right"
                    :label="$t('action.add')"
                    @click="addField" />
            </div>
        </PkFieldset>
    </div>
</template>

<style lang="scss">
    .pk-editor-json-schema {
        &__header {
            display: flex;
            justify-content: flex-end;
            align-items: center;
            gap: var(--spacing-8);
            margin-top: calc(-1 * var(--spacing-md));
            margin-inline: calc(-1 * var(--spacing-md));
            margin-bottom: var(--spacing-8);
            padding-inline: var(--input-spacing-right) var(--input-spacing-left);
        }

        &__root-type {
            border: 1px solid var(--color-surface-3);
            border-radius: var(--rounded-md);
        }

        &__tabs {
            border: 1px solid var(--color-surface-3);
            border-radius: var(--rounded-md);
        }

        &__visual-editor {
            display: flex;
            flex-direction: column;
            margin-inline: calc(-1 * var(--spacing-md));
        }

        &__array-hint {
            padding: var(--spacing-4) var(--input-spacing-right);
            font-size: var(--text-12);
            color: var(--color-word-3);
        }

        &__actions {
            min-height: var(--spacing-40);
            display: flex;
            justify-content: space-between;
            margin-inline: calc(-1 * var(--spacing-md));
            margin-bottom: calc(-1 * var(--spacing-md));
            padding: var(--input-spacing-top) var(--input-spacing-right)
                var(--input-spacing-bottom) var(--input-spacing-left);
            border-top: 1px solid var(--color-surface-3);
        }

        &--empty {
            .pk-editor-json-schema__visual-editor {
                padding-top: 0px;
                border-top: none;
            }
        }
    }
</style>
